"use client";

import { useEffect, useState } from "react";
import { CookieIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export function hasCookieConsent() {
  if (typeof document === "undefined") {
    return false;
  }
  return document.cookie?.includes("cookieConsent=true");
}

export function CookieConsent({
  variant = "default",
  onAcceptCallback = () => {},
  onDeclineCallback = () => {},
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [hide, setHide] = useState(false);

  const accept = () => {
    setIsOpen(false);
    document.cookie =
      "cookieConsent=true; expires=Fri, 31 Dec 9999 23:59:59 GMT";
    setTimeout(() => {
      setHide(true);
    }, 700);
    onAcceptCallback();
  };

  const decline = () => {
    setIsOpen(false);
    document.cookie =
      "cookieConsent=false; expires=Fri, 31 Dec 9999 23:59:59 GMT";
    setTimeout(() => {
      setHide(true);
    }, 700);
    onDeclineCallback();
  };

  useEffect(() => {
    const isWatcheroUserAgent =
      typeof navigator !== "undefined" &&
      navigator.userAgent.includes("Watchero");

    if (document.cookie?.includes("cookieConsent=") || isWatcheroUserAgent) {
      setIsOpen(false);
      setHide(true);
    } else {
      setIsOpen(true);
      setTimeout(() => {
        setHide(false);
      }, 700);
    }
  }, []);

  useEffect(() => {

    const handleCookieConsentOpen = () => {
      setHide(false);
      requestAnimationFrame(() => {
        setIsOpen(true);
      });
    };

    window.addEventListener("cookie-consent:open", handleCookieConsentOpen);

    return () => {
      window.removeEventListener(
        "cookie-consent:open",
        handleCookieConsentOpen,
      );
    };
  }, []);

  return variant === "default" ? (
    <div
      className={cn(
        "z-200 fixed inset-x-0 bottom-0 w-full p-4 duration-700 sm:bottom-4 sm:left-4 sm:max-w-md sm:p-0",
        !isOpen
          ? "translate-y-8 opacity-0 transition-[opacity,transform]"
          : "translate-y-0 opacity-100 transition-[opacity,transform]",
        hide && "hidden",
      )}
    >
      <div className="rounded-lg border border-border bg-background shadow-lg dark:bg-card sm:rounded-md">
        <div className="grid gap-2">
          <div className="flex h-12 items-center justify-between border-b border-border p-3 sm:h-14 sm:p-4">
            <h1 className="text-base font-medium sm:text-lg">We use cookies</h1>
            <CookieIcon className="size-4 sm:size-[1.2rem]" />
          </div>
          <div className="p-3 sm:p-4">
            <p className="text-start text-xs font-normal text-muted-foreground sm:text-sm">
              We use cookies to ensure you get the best experience on our
              website. For more information on how we use cookies, please see
              our cookie policy.
              <br />
              <br />
              <span className="text-xs">
                By clicking{" "}
                <span className="font-medium text-black dark:text-white">
                  Accept
                </span>
                , you agree to our use of cookies.
              </span>
              <br />
              <a href="/privacy" className="text-xs underline">
                Learn more.
              </a>
            </p>
          </div>
          <div className="grid grid-cols-2 items-center gap-2 border-t border-border p-3 dark:bg-background/20 sm:p-4 sm:py-5">
            <Button onClick={accept} variant="default" className="w-full">
              Accept
            </Button>
            <Button onClick={decline} variant="outline" className="w-full">
              Decline
            </Button>
          </div>
        </div>
      </div>
    </div>
  ) : variant === "small" ? (
    <div
      className={cn(
        "z-200 fixed inset-x-0 bottom-0 w-full p-4 duration-700 sm:bottom-4 sm:left-4 sm:max-w-md sm:p-0",
        !isOpen
          ? "translate-y-8 opacity-0 transition-[opacity,transform]"
          : "translate-y-0 opacity-100 transition-[opacity,transform]",
        hide && "hidden",
      )}
    >
      <div className="m-0 rounded-lg border border-border bg-background shadow-lg dark:bg-card sm:m-3">
        <div className="flex items-center justify-between p-3">
          <h1 className="text-base font-medium sm:text-lg">We use cookies</h1>
          <CookieIcon className="size-4 sm:size-[1.2rem]" />
        </div>
        <div className="-mt-2 p-3">
          <p className="text-left text-xs text-muted-foreground sm:text-sm">
            We use cookies to ensure you get the best experience on our website.
            For more information on how we use cookies, please see our
            <a href="/privacy" className="underline">
              privacy policy
            </a>
            .
          </p>
        </div>
        <div className="mt-2 grid grid-cols-2 items-center gap-2 border-t p-3">
          <Button onClick={accept} className="w-full">
            Accept
          </Button>
          <Button onClick={decline} className="w-full" variant="outline">
            Decline
          </Button>
        </div>
      </div>
    </div>
  ) : (
    variant === "minimal" && (
      <div
        className={cn(
          "z-200 fixed inset-x-0 bottom-0 w-full p-4 duration-700 sm:bottom-4 sm:left-4 sm:max-w-[300px] sm:p-0",
          !isOpen
            ? "translate-y-8 opacity-0 transition-[opacity,transform]"
            : "translate-y-0 opacity-100 transition-[opacity,transform]",
          hide && "hidden",
        )}
      >
        <div className="m-0 rounded-lg border border-border bg-background shadow-lg dark:bg-card sm:m-3">
          <div className="flex items-center justify-between border-b border-border p-3">
            <div className="flex items-center gap-2">
              <CookieIcon className="size-3 sm:size-4" />
              <span className="text-xs font-medium sm:text-sm">
                Cookie Notice
              </span>
            </div>
          </div>
          <div className="p-3">
            <p className="text-[11px] text-muted-foreground sm:text-xs">
              We use cookies to enhance your browsing experience.
              <a href="/privacy" className="underline sm:text-xs">
                Learn more.
              </a>
            </p>
            <div className="mt-3 grid grid-cols-2 items-center gap-2">
              <Button onClick={accept} variant="default" className="w-full">
                Accept
              </Button>
              <Button onClick={decline} variant="ghost" className="w-full">
                Decline
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  );
}
